import React, { useEffect, useRef, useState } from "react";
import { Box, CircularProgress, Divider, Grid, Stack, Typography } from "@mui/material";
import { EventAvailable } from "@mui/icons-material";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { addMokshaReservation, listAllHotelAgents, resetAllHotelAgents } from "../../store/actions";
import { reservationsApi } from "../../services/moksha-reservations/reservationsApi";
import { isPmsConnected } from "../../utils/isLogged";
import ReservationLine from "./ReservationLine";
import StayDates from "./StayDates";
import AvailabilityTable from "./AvailabilityTable";
import GuestSection from "./GuestSection";
import SummaryPanel, { MobileSubmitBar } from "./SummaryPanel";
import { MOBILE_BAR_HEIGHT, PageTitle, panelSx, SectionTitle, useColors } from "./ui";
import { makeField } from "./presentation";
import { useFocusFirstInvalid } from "./focus";
import {
    conflictFieldIds,
    deriveDraft,
    emptyDraft,
    fieldIds,
    newLine,
    roomtypeOf,
    toRequestBody,
    withConflicts,
    withRatesCleared,
} from "./reservationDraft";

function MokshaReservation() {
    const colors = useColors();
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const focusFirstInvalid = useFocusFirstInvalid();

    const loggedInUser = useSelector((state) => state.auth.loggedInUser);
    const agentsList = useSelector((state) => state.agents.agents.list);
    const isLoading = useSelector((state) => state.loading);
    const isMokshaStaff = loggedInUser?.is_moksha_staff === true;
    const pmsConnected = isPmsConnected();
    const canReserve = isMokshaStaff && pmsConnected;

    const [draft, setDraft] = useState(emptyDraft);
    const [checking, setChecking] = useState(false);
    const [availability, setAvailability] = useState(null);
    const [checkedDates, setCheckedDates] = useState(null);
    const [availabilityError, setAvailabilityError] = useState("");
    const [touched, setTouched] = useState({});
    const [submitted, setSubmitted] = useState(false);
    const [serverError, setServerError] = useState("");
    const [newLineKey, setNewLineKey] = useState(null);
    const formRef = useRef();
    const problemRef = useRef();

    const derived = deriveDraft(draft, availability, checkedDates);

    // The flag arrives with the logged in user; firing before it lands risks the 401 logout.
    useEffect(() => {
        if (!canReserve) return;
        dispatch(resetAllHotelAgents());
        dispatch(listAllHotelAgents({ isForDropdown: true }));
        // eslint-disable-next-line
    }, [canReserve]);

    const patch = (changes) => setDraft((prev) => ({ ...prev, ...changes }));
    const setGuestField = (key, value) => setDraft((prev) => ({ ...prev, guest: { ...prev.guest, [key]: value } }));
    const setLines = (update) => setDraft((prev) => ({ ...prev, lines: update(prev.lines) }));
    const touch = (id) => setTouched((prev) => (prev[id] ? prev : { ...prev, [id]: true }));
    const touchAll = (ids) => setTouched((prev) => ({ ...prev, ...Object.fromEntries(ids.map((id) => [id, true])) }));
    const field = makeField(derived.errors, touched, touch);

    const addLine = (roomtype) => {
        const line = newLine(roomtype);
        setNewLineKey(line.key);
        setLines((prev) => [...prev, line]);
    };

    const checkAvailability = async () => {
        if (!derived.datesValid || checking) return;
        setChecking(true);
        setAvailabilityError("");
        setServerError("");
        const response = await reservationsApi.getAvailability({
            checkin_date: draft.checkin,
            checkout_date: draft.checkout,
        });
        setChecking(false);
        if (!response.ok) {
            setAvailabilityError(
                response.message || "Availability could not be loaded. Check your connection and try again."
            );
            return;
        }
        if (derived.stale) setLines(withRatesCleared);
        setAvailability(response.data);
        setCheckedDates({ checkin: draft.checkin, checkout: draft.checkout });
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setSubmitted(true);
        setServerError("");
        touchAll(fieldIds(draft, derived.nights));
        if (derived.formProblem || derived.errorCount) {
            focusFirstInvalid(formRef.current, problemRef.current);
            return;
        }

        const response = await dispatch(addMokshaReservation(toRequestBody(draft, derived.nights)));
        if (response?.ok) {
            navigate(`/booking-details/${response.data.booking_id}`);
            return;
        }
        setServerError(response?.message || "The booking was not created. Try again.");
        if (response?.conflicts?.length) {
            setAvailability((prev) => withConflicts(prev, response.conflicts));
            touchAll(conflictFieldIds(draft.lines, response.conflicts));
        }
        focusFirstInvalid(formRef.current, problemRef.current);
    };

    const panel = panelSx(colors);

    if (!loggedInUser) {
        return (
            <Box m="20px" display="flex" justifyContent="center">
                <CircularProgress color="secondary" />
            </Box>
        );
    }

    if (!isMokshaStaff) {
        return (
            <Box m="20px">
                <PageTitle badge={false} />
                <Box sx={panel}>
                    <Typography>This page is only available to Moksha staff.</Typography>
                </Box>
            </Box>
        );
    }

    if (!pmsConnected) {
        return (
            <Box m="20px">
                <PageTitle badge={false} />
                <Box sx={panel}>
                    <Typography>Moksha reservations are only available for properties connected to a PMS.</Typography>
                </Box>
            </Box>
        );
    }

    return (
        <Box
            m="20px"
            sx={{ mb: { xs: `calc(${MOBILE_BAR_HEIGHT}px + 20px + env(safe-area-inset-bottom))`, md: 15 } }}
        >
            <PageTitle />

            <Box sx={panel} mb={2}>
                <StayDates
                    checkin={draft.checkin}
                    checkout={draft.checkout}
                    derived={derived}
                    checking={checking}
                    hasResults={!!availability}
                    hasLines={draft.lines.length > 0}
                    error={availabilityError}
                    onPatch={patch}
                    onCheck={checkAvailability}
                />
                <Divider sx={{ my: 2 }} />
                {!availability ? (
                    <Stack alignItems="center" spacing={1} py={3} textAlign="center">
                        <EventAvailable sx={{ fontSize: 40, color: colors.grey[400] }} />
                        <Typography color={colors.grey[300]}>
                            Pick check-in and check-out dates to see which rooms are free.
                        </Typography>
                    </Stack>
                ) : (
                    <AvailabilityTable
                        availability={availability}
                        checkedDates={checkedDates}
                        lines={draft.lines}
                        stale={derived.stale}
                        onAdd={addLine}
                    />
                )}
            </Box>

            {availability && (
                <form ref={formRef} onSubmit={handleSubmit} noValidate>
                    <Grid container spacing={2}>
                        <Grid item md={8} xs={12}>
                            <Stack spacing={2}>
                                <Box sx={panel}>
                                    <SectionTitle>Rooms</SectionTitle>
                                    {!draft.lines.length ? (
                                        <Typography color={colors.grey[300]}>
                                            Select "Add" next to a roomtype above to book it.
                                        </Typography>
                                    ) : (
                                        <Stack spacing={2}>
                                            {draft.lines.map((line) => (
                                                <ReservationLine
                                                    key={line.key}
                                                    line={line}
                                                    roomtype={roomtypeOf(availability, line.roomtypeId)}
                                                    nights={derived.nights}
                                                    field={field}
                                                    autoFocus={line.key === newLineKey}
                                                    onChange={(next) =>
                                                        setLines((prev) => prev.map((l) => (l.key === line.key ? next : l)))
                                                    }
                                                    onRemove={() => setLines((prev) => prev.filter((l) => l.key !== line.key))}
                                                />
                                            ))}
                                        </Stack>
                                    )}
                                </Box>
                                <GuestSection
                                    draft={draft}
                                    capacity={derived.capacity}
                                    agentsList={agentsList}
                                    field={field}
                                    onGuestField={setGuestField}
                                    onPatch={patch}
                                />
                            </Stack>
                        </Grid>
                        <Grid item md={4} xs={12}>
                            <SummaryPanel
                                derived={derived}
                                basis={draft.basis}
                                submitted={submitted}
                                serverError={serverError}
                                problemRef={problemRef}
                                isSaving={isLoading}
                                field={field}
                                onPatch={patch}
                            />
                        </Grid>
                    </Grid>
                    <MobileSubmitBar derived={derived} isSaving={isLoading} />
                </form>
            )}
        </Box>
    );
}

export default MokshaReservation;
